/** 957 → "957", 5_412 → "5.4K", 1_100_000 → "1.1M" (beeg-style counters). */
export function compact(n: number): string {
  if (n < 1000) return String(Math.max(0, Math.round(n)));
  const units: [number, string][] = [
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  for (let i = 0; i < units.length; i++) {
    const [size, suffix] = units[i];
    if (n < size) continue;
    const x = n / size;
    const rounded = x < 10 ? Math.round(x * 10) / 10 : Math.round(x);
    if (rounded >= 1000 && i > 0) return `${Math.round(rounded / 1000)}${units[i - 1][1]}`;
    return `${rounded}${suffix}`;
  }
  return String(n);
}

/** A text cut at a word to at most `max` characters, "…" marking the cut (meta descriptions). */
export const clip = (text: string, max: number) => (text.length <= max ? text : `${text.slice(0, text.lastIndexOf(" ", max - 1))}…`);

/** Seconds → "4:05" / "1:02:09". */
export function duration(total: number): string {
  const s = Math.max(0, Math.floor(total));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

/** How each unit reads, "{n}" for the number (the interface's words, lib/locales `ago`). */
export interface AgeUnits {
  now: string;
  m: string;
  h: string;
  d: string;
  w: string;
  mo: string;
  y: string;
}
const ENGLISH_AGE: AgeUnits = { now: "now", m: "{n}m", h: "{n}h", d: "{n}d", w: "{n}w", mo: "{n}mo", y: "{n}y" };

/** ISO date → "now", "5h", "4d", "3w", "2mo", "1y" (or the same in `units`). */
export function age(iso: string, units: AgeUnits = ENGLISH_AGE, now = Date.now()): string {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const unit = (key: Exclude<keyof AgeUnits, "now">, n: number) => units[key].replace("{n}", String(n));
  const min = Math.floor(diff / 60_000);
  if (min < 1) return units.now;
  if (min < 60) return unit("m", min);
  const h = Math.floor(min / 60);
  if (h < 24) return unit("h", h);
  const d = Math.floor(h / 24);
  if (d < 14) return unit("d", d);
  if (d < 30) return unit("w", Math.floor(d / 7));
  if (d < 365) return unit("mo", Math.floor(d / 30));
  return unit("y", Math.floor(d / 365));
}

export function initials(name: string): string {
  // NFKC turns styled letters (𝒩𝒾𝒸𝑜𝓁) into plain ones; letters are taken whole, never half a surrogate pair.
  const words = name.normalize("NFKC").replace(/[^\p{L}\p{N}\s]/gu, "").split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = (w: string) => Array.from(w)[0] ?? "";
  if (words.length === 1) return first(words[0]).toUpperCase();
  return (first(words[0]) + first(words[1])).toUpperCase();
}

/** The first letter of a name, whole and plain (see initials). */
export const firstLetter = (name: string) => (Array.from(name.normalize("NFKC").trim())[0] ?? "?").toUpperCase();

/** Time spent, for stats: "45m", "5h", "12h 5m", "1,240h 3m". */
export function hoursMinutes(total: number): string {
  const s = Math.max(0, Math.floor(total));
  if (s < 60) return s ? "<1m" : "0m";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${new Intl.NumberFormat("en-US").format(h)}h${m ? ` ${m}m` : ""}` : `${m}m`;
}
